import { memo as Memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

//* Components
import CustomLink from 'components/common/CustomLink';
import Icon from 'components/common/Icon';
import Image from 'components/common/Image';
import Text from 'components/common/Text';

//* Style
import MenuItemStyle from './style';

const MenuItem = Memo(({ title, slug, type, children, selectedLang, regions }) => {
	//! Router
	const router = useRouter();

	//! Refs
	const itemRef = useRef();

	//! States
	const [activeMenu, setActiveMenu] = useState();
	const [activeInfo, setActiveInfo] = useState(0);

	//! Window Click Listener for close
	const handleWindowClick = useCallback(
		(e) => {
			if (!itemRef.current.contains(e.target)) {
				setActiveMenu(false);
			}
		},
		[activeInfo]
	);

	//! Did Mount
	useEffect(() => {
		window.addEventListener('click', handleWindowClick);

		return () => {
			window.removeEventListener('click', handleWindowClick);
		};
	}, []);

	//! Child Menus
	const childrenPopup = useMemo(() => {
		switch (type) {
			case 'icon-menu':
				return (
					<ul className='menu-inner-cont icon-menu'>
						{children.map((child, k) => (
							<li
								key={k}
								className={`menu-inner-item ${router.asPath === `/${slug}/${child.slug}/` ? 'active' : ''}`}
							>
								<CustomLink
									url={`/${slug}/${child.slug}`}
									className='p pS'
								>
									<Icon name={child.icon} />
									{child.title}
								</CustomLink>
							</li>
						))}
					</ul>
				);
			case 'sub-menu':
				return (
					<ul className='menu-inner-cont sub-menu'>
						{children.map((child, k) => (
							<li
								key={k}
								className={`menu-inner-item sub-menu-inner-item`}
							>
								<div className='sub-menu-items-cont'>
									<Text
										className='sub-menu-title font-ping-bold primaryColor1'
										text={child.title}
									/>

									<div className='sub-menu-items'>
										{child.children.map((subItem, i) => (
											<CustomLink
												key={i}
												url={`/${slug}/${subItem.slug}`}
												className={`p pS menu-inner-item ${router.asPath === `/${slug}/${subItem.slug}/` ? 'active' : ''}`}
											>
												{subItem.title}
											</CustomLink>
										))}
									</div>
								</div>
							</li>
						))}
					</ul>
				);
			case 'info-menu':
				const infos = [];
				return (
					<ul className='menu-inner-cont info-menu'>
						{children.map((child, k) => {
							infos.push(
								<div
									key={k}
									className={`menu-item-info ${activeInfo === k ? 'active' : ''}`}
								>
									<div className='image'>
										<Image
											src={child.featured_image?.url || '/images/default.jpeg'}
											alt={child?.short_info?.title}
											objectFit={'cover'}
										/>
									</div>

									<div className='info'>
										<Text
											className='h6 info-title'
											text={child?.title}
										/>
										<Text
											className='p'
											text={child?.featured_description}
										/>
									</div>
								</div>
							);

							return (
								<li
									key={k}
									className={`menu-inner-item ${router.asPath === `/${slug}/${child.slug}/` ? 'active' : ''}`}
								>
									<CustomLink
										url={`/${slug}/${child.slug}`}
										className='p pS'
										onMouseEnter={() => setActiveInfo(k)}
									>
										{child.title}
									</CustomLink>
								</li>
							);
						})}
						<li
							key={children.length}
							className='menu-inner-item-info'
						>
							<div className={`default-menu-item-info`}>
								<span className='fake-image-cont'>
									<span className='fake-image' />
								</span>
							</div>
							{infos}
						</li>
					</ul>
				);
			default:
				return (
					<ul className='menu-inner-cont default-menu'>
						{children.map((child, k) => (
							<li
								key={k}
								className={`menu-inner-item ${router.asPath === `/${slug}/${child.slug}/` ? 'active' : ''}`}
							>
								<CustomLink
									url={`/${slug}/${child.slug}`}
									className='p pS'
								>
									{child.title}
								</CustomLink>
							</li>
						))}
					</ul>
				);
		}
	}, [type, children, activeInfo, router]);

	return (
		<MenuItemStyle
			onMouseLeave={() => setActiveInfo(0)}
			ref={itemRef}
			onClick={() => setActiveMenu(!activeMenu)}
			className={`lang-${selectedLang}  ${router.asPath.includes(slug) ? 'active' : ''} menu-item-cont font-ping-medium p pS ${activeMenu ? 'opened' : ''}`}
		>
			<span>{title}</span>

			<Icon
				className='p pXS'
				name='arrow-small'
			/>

			{childrenPopup}
		</MenuItemStyle>
	);
});

export default MenuItem;
